import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';

import UserAvatarV2 from 'common/common/UserAvatarV2';
import {
  type FailedFile,
  LoadStatus,
  type LocalFile,
  type UploadedFile,
  type UploadingFile,
} from 'common/constants/files';
import { CompanyContext } from 'common/containers/CompanyContainer';
import { ShowToastContext, ToastTypes } from 'common/containers/ToastContainer';
import { ViewerContext } from 'common/containers/ViewerContainer';
import ErrorMessage from 'common/ErrorMessage';
import FileRenderer from 'common/file/FileRenderer';
import { createFiles, createImageFiles } from 'common/file/utils/createFiles';
import getAcceptedMimeTypes from 'common/file/utils/getAcceptedMimeTypes';
import getValidFileURLs from 'common/file/utils/getValidFileURLs';
import uploadFile, { numberOfFilesLimit } from 'common/file/utils/uploadFile';
import Form from 'common/Form';
import MentionsTextarea from 'common/inputs/MentionsTextarea';
import UploadFileButton from 'common/inputs/UploadFileButton';
import { cn } from 'common/style/utils';
import {
  useCreateIdeaComment,
  useUpdateIdeaComment,
} from 'common/subdomain/admin/v2/hooks/useIdeaCommentMutations';
import Flex from 'common/ui/Flex';
import HStack from 'common/ui/HStack';
import VStack from 'common/ui/VStack';
import Button from 'common/uiV3/Button';

import type { QueryParams as IdeaActivityQueryParams } from 'common/actions/ideaActivityQueries';
import type { Idea } from 'common/api/endpoints/idea';
import type { IdeaComment } from 'common/api/endpoints/ideaActivity';
import type { MentionSuggestion } from 'common/api/endpoints/mentions';

type CommentComposerProps = {
  comment?: IdeaComment;
  activityQueryParams: IdeaActivityQueryParams;
  autoFocus?: boolean;
  className?: string;
  idea: Idea;
  initialMentionedUsers?: MentionSuggestion[];
  placeholder?: string;
  showAvatar?: boolean;
  showCancel?: boolean;
  onCancel?: () => void;
  onSaved?: () => void;
};

const CommentComposer = ({
  comment,
  activityQueryParams,
  autoFocus = false,
  className,
  idea,
  initialMentionedUsers = [],
  placeholder = 'Leave a comment',
  showAvatar = true,
  showCancel = false,
  onCancel,
  onSaved,
}: CommentComposerProps) => {
  const company = useContext(CompanyContext);
  const viewer = useContext(ViewerContext);
  const showToast = useContext(ShowToastContext);

  const [value, setValue] = useState(comment?.value ?? '');
  const [files, setFiles] = useState<LocalFile[]>(
    comment?.files
      ? [...createFiles(comment?.files || []), ...createImageFiles(comment?.imageURLs || [])]
      : [],
  );
  const [isFocused, setIsFocused] = useState(autoFocus);

  const textareaRef = useRef<any>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const editComment = useUpdateIdeaComment(activityQueryParams, {
    onSuccess: () => {
      setValue('');
      setFiles([]);
      setIsFocused(false);
      editComment.reset();
      onSaved?.();
    },
  });

  const createComment = useCreateIdeaComment(activityQueryParams, {
    onSuccess: () => {
      setValue('');
      setFiles([]);
      setIsFocused(false);
      createComment.reset();
      onSaved?.();
    },
  });

  const areFilesUploading = useCallback(() => {
    return files.some((file) => file.uploadStatus !== LoadStatus.loaded);
  }, [files]);

  const hasContent = useCallback(() => {
    return !!value || files.length > 0;
  }, [value, files]);

  const onFileError = useCallback(
    (file: FailedFile, errorMessage: string) => {
      showToast(errorMessage, ToastTypes.error);
      setFiles((prev) => prev.filter((f) => f.uniqueID !== file.uniqueID));
    },
    [showToast],
  );

  const onFileUploading = useCallback((file: UploadingFile) => {
    setFiles((prev) => [...prev, file]);
  }, []);

  const onFileUploaded = useCallback((file: UploadedFile) => {
    setFiles((prev) => prev.map((f) => (f.uniqueID === file.uniqueID ? file : f)));
  }, []);

  const onFileRemoved = useCallback((file: LocalFile) => {
    setFiles((prev) => prev.filter((f) => f.uniqueID !== file.uniqueID));
  }, []);

  const onFile = useCallback(
    async (file: File) => {
      if (files.length >= numberOfFilesLimit) {
        showToast('You have reached the file limit.', ToastTypes.error);
        return;
      }

      await uploadFile({
        file,
        viewer,
        onFileError,
        onFileUploading,
        onFileUploaded,
      });
    },
    [files.length, showToast, viewer, onFileError, onFileUploading, onFileUploaded],
  );

  const onFileStart = useCallback(() => {
    if (files.length >= numberOfFilesLimit) {
      showToast('You have reached the file limit.', ToastTypes.error);
      return false;
    }
    return true;
  }, [files.length, showToast]);

  const onSubmit = useCallback(() => {
    const { imageURLs, nonImageFileURLs } = getValidFileURLs(files);

    if (!value && imageURLs.length === 0 && nonImageFileURLs.length === 0) {
      showToast('Oops! You forgot to enter a comment.', ToastTypes.error);
      return;
    }

    if (comment) {
      editComment.mutate({
        commentID: comment._id,
        fileURLs: JSON.stringify(nonImageFileURLs),
        imageURLs: JSON.stringify(imageURLs),
        value,
      });
      return;
    }

    createComment.mutate({
      ideaID: idea._id,
      fileURLs: JSON.stringify(nonImageFileURLs),
      imageURLs: JSON.stringify(imageURLs),
      value,
    });
  }, [files, value, idea._id, createComment, editComment, comment, showToast]);

  const onFocus = useCallback(() => {
    setIsFocused(true);
  }, []);

  useEffect(() => {
    if (!isFocused) {
      return;
    }

    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsFocused(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isFocused]);

  const showButtonBar = isFocused || hasContent();
  const submitDisabled = !hasContent() || areFilesUploading() || createComment.isLoading;
  const errorMessage = createComment.error?.message;

  return (
    <HStack
      ref={containerRef}
      className={cn('gap-3 base-border-t bg-background pl-5 pr-3 py-4', className)}>
      {showAvatar && <UserAvatarV2 user={viewer} size={28} />}
      <Form
        acceptedFileTypes={getAcceptedMimeTypes(company)}
        addEventsToDocument={false}
        allowFileUpload={showButtonBar}
        className="flex-1 mt-[3px]"
        disableSubmit={submitDisabled}
        onFile={onFile}
        onFileStart={onFileStart}
        onSubmit={onSubmit}>
        <VStack className="gap-2">
          <HStack className="gap-2 justify-between">
            <MentionsTextarea
              ref={textareaRef}
              autoFocus={autoFocus}
              isInternal={true}
              initialMentionedUsers={initialMentionedUsers}
              onChange={setValue}
              onFocus={onFocus}
              placeholder={placeholder}
              className={cn(
                'w-full **:data-[slot=textarea]:!border-0',
                '**:data-[slot=textarea]:!p-0',
                '**:data-[slot=backdrop]:!p-0',
              )}
              portalPosition="top"
              value={value}
            />
            {showButtonBar && (
              <Flex className="items-center">
                <UploadFileButton
                  acceptedMimeTypes={getAcceptedMimeTypes(company)}
                  defaultStyle={false}
                  onFileError={onFileError}
                  onFileStart={onFileStart}
                  onFileUploading={onFileUploading}
                  onFileUploaded={onFileUploaded}
                />
              </Flex>
            )}
          </HStack>
          {files.length > 0 && (
            <FileRenderer allowRemove={true} files={files} onFileRemoved={onFileRemoved} />
          )}
          {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
        </VStack>
        {showButtonBar && (
          <HStack className="items-center justify-end gap-3 pt-2">
            {showCancel && (
              <Button variant="outlined" onClick={onCancel}>
                Cancel
              </Button>
            )}
            <Button disabled={submitDisabled} isLoading={createComment.isLoading}>
              Submit
            </Button>
          </HStack>
        )}
      </Form>
    </HStack>
  );
};

export default CommentComposer;
